import type { GetMyClassesData } from '@dataconnect/generated';

import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';
import { useGetMyClasses, useGetClassForView, useGetMySchoolYears } from '@dataconnect/generated/react';

import { useManagedCourses, type ActiveCourse } from './use-managed-courses';

// ----------------------------------------------------------------------

/**
 * A single class from the teacher's `GetMyClasses` query — the source of truth
 * for the app-wide "active class". A teacher works inside exactly one class at a
 * time; this context centralises that selection so every dashboard view and the
 * header switcher share it (and so it survives navigation + reload).
 */
export type TeacherClass = GetMyClassesData['classes'][number];

/** A school the teacher has at least one class in, OR is enrolled in as staff. */
export type TeacherSchool = { id: string; name: string };

/**
 * A (school, year) the teacher can act in — the option shape for the combined
 * School·Year switcher. Union of the years reached via staff membership
 * (`GetMySchoolYears`) and via the teacher's own classes.
 */
export type SchoolYearOption = {
  yearId: string;
  yearName: string;
  status: string | null;
  schoolId: string;
  schoolName: string;
};

export type { ActiveCourse } from './use-managed-courses';

/**
 * The bell-schedule period this class meets in under the school's *default* daily
 * schedule, or null if none is assigned. A class can have a different period per
 * daily schedule; the default schedule answers "when does it normally meet".
 */
export function defaultSchedulePeriod(c: TeacherClass) {
  return c.schedulePeriods?.find((sp) => sp.dailySchedule.isDefault)?.schedulePeriod ?? null;
}

/** Normalize a class's nested course into the app-wide `ActiveCourse` shape. */
function asActiveCourse(c: TeacherClass['course']): ActiveCourse {
  return {
    id: c.id,
    name: c.name,
    subject: c.subject ?? null,
    gradeLevel: c.gradeLevel ?? null,
    schoolYear: {
      id: c.schoolYear.id,
      name: c.schoolYear.name,
      school: { id: c.schoolYear.school.id, name: c.schoolYear.school.name },
    },
  };
}

const SCHOOL_KEY = 'teacher.activeSchoolId';
const CLASS_KEY = 'teacher.activeClassId';
const COURSE_KEY = 'teacher.activeCourseId';
const YEAR_KEY = 'teacher.activeSchoolYearId';
// A colleague's class opened from the Department page (epic #566). Per tab (sessionStorage):
// view-only mode never outlives the tab, and a reload keeps it.
const VIEW_KEY = 'teacher.viewingClassId';

function readView(): string | null {
  try {
    return sessionStorage.getItem(VIEW_KEY);
  } catch {
    return null;
  }
}

function persistView(value: string | null) {
  try {
    if (value) sessionStorage.setItem(VIEW_KEY, value);
    else sessionStorage.removeItem(VIEW_KEY);
  } catch {
    /* storage unavailable — view mode just won't survive a reload */
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persist(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — selection just won't persist */
  }
}

function schoolOf(c: TeacherClass): TeacherSchool {
  return c.course.schoolYear.school;
}

/** The colleague's class being viewed (epic #566), with whose class it is. */
export type ViewingClass = { classId: string; teacherName: string; teacherUid: string | null };

type ClassContextValue = {
  schools: TeacherSchool[];
  activeSchool: TeacherSchool | null;
  activeSchoolId: string | null;
  setActiveSchoolId: (id: string) => void;
  /** Classes within the active school — what the class switcher shows. */
  classes: TeacherClass[];
  activeClass: TeacherClass | null;
  activeClassId: string | null;
  setActiveClassId: (id: string) => void;
  /** `GetMyClasses` load state — the teaching context. */
  isLoading: boolean;

  // ── View-only mode (Department / Team Lead, epic #566) ──
  /**
   * Set while a lead views a colleague's class from the Department page: `activeClass` is that
   * class, and every class page is read-only (live help excepted). Null for your own class.
   */
  viewing: ViewingClass | null;
  /** Open a colleague's class in view-only mode (the Department page's "Open"). */
  startViewing: (classId: string) => void;
  /** Leave view-only mode, back to your own active class. */
  stopViewing: () => void;

  // ── Course-authoring scope (decoupled from classes; epic #143 Risk #2) ──
  /** Courses the teacher may author in the active school year (managed-courses seam). */
  courses: ActiveCourse[];
  activeCourse: ActiveCourse | null;
  activeCourseId: string | null;
  setActiveCourseId: (id: string) => void;
  /** The active school year (an explicitly-picked year, else the class's / ACTIVE membership year). */
  activeSchoolYearId: string | null;
  /** The (school, year) options for the combined School·Year switcher. */
  schoolYearOptions: SchoolYearOption[];
  /** Pick a school year (implies its school) — resets class + course to that (school, year). */
  setActiveSchoolYearId: (yearId: string) => void;
  /** Membership + course-list load state — the authoring context. */
  coursesLoading: boolean;
};

const ClassContext = createContext<ClassContextValue | undefined>(undefined);

// ----------------------------------------------------------------------

export function ClassProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending } = useGetMyClasses();
  const allClasses = useMemo(() => data?.classes ?? [], [data]);

  // Class-independent school-year membership (works for a staff member who teaches no class).
  const { data: yearsData, isPending: yearsPending } = useGetMySchoolYears();
  const myYears = useMemo(() => (yearsData?.schoolYearUsers ?? []).map((r) => r.schoolYear), [yearsData]);

  const [storedSchoolId, setStoredSchoolId] = useState<string | null>(() => read(SCHOOL_KEY));
  const [storedClassId, setStoredClassId] = useState<string | null>(() => read(CLASS_KEY));
  const [storedCourseId, setStoredCourseId] = useState<string | null>(() => read(COURSE_KEY));
  const [storedYearId, setStoredYearId] = useState<string | null>(() => read(YEAR_KEY));
  const [viewId, setViewId] = useState<string | null>(() => readView());

  // The viewed colleague's class, in GetMyClasses' shape. A class you teach is never "viewed".
  const viewQuery = useGetClassForView({ id: viewId ?? '' }, { enabled: !!viewId });
  const viewedRow = viewId ? viewQuery.data?.class ?? null : null;
  const viewPending = !!viewId && viewQuery.isPending;

  // Unique schools across the teacher's classes AND their staff memberships, so a
  // class-less Department Head still has a school (and thus a school year) to act in.
  const schools = useMemo(() => {
    const m = new Map<string, TeacherSchool>();
    for (const c of allClasses) {
      const s = schoolOf(c);
      if (s && !m.has(s.id)) m.set(s.id, { id: s.id, name: s.name });
    }
    for (const y of myYears) {
      const s = y.school;
      if (s && !m.has(s.id)) m.set(s.id, { id: s.id, name: s.name });
    }
    return [...m.values()];
  }, [allClasses, myYears]);

  // Every (school, year) the teacher can act in — union of staff-membership years
  // (with a `status`) and the years their own classes live in. Membership wins on
  // dedupe (it carries the ACTIVE/DRAFT status the class nesting lacks).
  const schoolYearOptions = useMemo(() => {
    const m = new Map<string, SchoolYearOption>();
    for (const y of myYears) {
      if (!m.has(y.id)) {
        m.set(y.id, {
          yearId: y.id,
          yearName: y.name,
          status: y.status ?? null,
          schoolId: y.school.id,
          schoolName: y.school.name,
        });
      }
    }
    for (const c of allClasses) {
      const sy = c.course.schoolYear;
      if (!m.has(sy.id)) {
        m.set(sy.id, {
          yearId: sy.id,
          yearName: sy.name,
          status: null,
          schoolId: sy.school.id,
          schoolName: sy.school.name,
        });
      }
    }
    return [...m.values()];
  }, [myYears, allClasses]);

  // Resolution pivots on the school YEAR. An explicitly-picked year (stored) drives both the
  // school and the year; with nothing stored we fall back to the year the teacher teaches in
  // (their class's year), else the school's ACTIVE membership year.
  const storedYearOption = useMemo(
    () => (storedYearId ? schoolYearOptions.find((o) => o.yearId === storedYearId) ?? null : null),
    [storedYearId, schoolYearOptions]
  );

  // Preferred school before the year is resolved: the stored year's school wins, then a
  // stored school, then the stored class's school, then the first school. (Rosters change
  // year to year, so a persisted id may no longer be valid.)
  const prefSchoolId =
    storedYearOption?.schoolId ??
    schools.find((s) => s.id === storedSchoolId)?.id ??
    allClasses.find((c) => c.id === storedClassId)?.course.schoolYear.school.id ??
    schools[0]?.id ??
    null;

  // Active school year: a valid stored year wins; else the year of the stored/first class in
  // the preferred school (the year you *teach* in — the pre-existing default); else the
  // preferred school's ACTIVE membership year (fallback: its first option). The last branch
  // makes a class-less Department Head resolvable.
  const activeSchoolYearId = useMemo(() => {
    if (storedYearOption) return storedYearOption.yearId;
    const inSchool = (c: TeacherClass) => schoolOf(c).id === prefSchoolId;
    const classForYear =
      allClasses.find((c) => c.id === storedClassId && inSchool(c)) ?? allClasses.find(inSchool);
    if (classForYear) return classForYear.course.schoolYear.id;
    const forSchool = schoolYearOptions.filter((o) => o.schoolId === prefSchoolId);
    return (forSchool.find((o) => o.status === 'ACTIVE') ?? forSchool[0])?.yearId ?? null;
  }, [storedYearOption, storedClassId, allClasses, schoolYearOptions, prefSchoolId]);

  // The active school follows the resolved year (a year belongs to one school); fall back to
  // the preferred school when there is no year at all.
  const activeYearOption = useMemo(
    () => schoolYearOptions.find((o) => o.yearId === activeSchoolYearId) ?? null,
    [schoolYearOptions, activeSchoolYearId]
  );
  const activeSchoolId = activeYearOption?.schoolId ?? prefSchoolId;
  const activeSchool = schools.find((s) => s.id === activeSchoolId) ?? null;

  // Classes within the active school AND year — drives the class switcher.
  const classes = useMemo(
    () =>
      allClasses.filter(
        (c) => schoolOf(c).id === activeSchoolId && c.course.schoolYear.id === activeSchoolYearId
      ),
    [allClasses, activeSchoolId, activeSchoolYearId]
  );

  // Resolve your own active class within the active (school, year)…
  const ownActiveClass =
    (storedClassId && classes.find((c) => c.id === storedClassId)) || classes[0] || null;
  // …unless you're viewing a colleague's class (epic #566). While that class loads there is
  // no active class (isLoading), so pages never flash your own class first.
  const ownsViewed = !!viewedRow && allClasses.some((c) => c.id === viewedRow.id);
  const viewing = useMemo<ViewingClass | null>(
    () =>
      viewedRow && !ownsViewed
        ? {
            classId: viewedRow.id,
            teacherName: viewedRow.teacher?.displayName ?? viewedRow.teacher?.email ?? 'another teacher',
            teacherUid: viewedRow.teacher?.uid ?? null,
          }
        : null,
    [viewedRow, ownsViewed]
  );
  const activeClass: TeacherClass | null = viewing ? viewedRow : viewPending ? null : ownActiveClass;
  const activeClassId = activeClass?.id ?? null;

  // The nested {id,name,school{id,name}} for the active year, to graft onto flat course rows.
  const activeSchoolYearMeta = useMemo<ActiveCourse['schoolYear'] | null>(
    () =>
      activeYearOption
        ? {
            id: activeYearOption.yearId,
            name: activeYearOption.yearName,
            school: { id: activeYearOption.schoolId, name: activeYearOption.schoolName },
          }
        : null,
    [activeYearOption]
  );

  const { courses, isPending: coursesPending } = useManagedCourses(activeSchoolYearId, activeSchoolYearMeta);

  // Resolve the active course: a stored id that still exists → the active class's course
  // (immediately, before the course list loads) → the first managed course → null.
  const activeCourse = useMemo<ActiveCourse | null>(() => {
    if (viewing && activeClass) return asActiveCourse(activeClass.course);
    const fromStore = storedCourseId ? courses.find((c) => c.id === storedCourseId) : null;
    if (fromStore) return fromStore;
    if (activeClass) return asActiveCourse(activeClass.course);
    return courses[0] ?? null;
  }, [viewing, storedCourseId, courses, activeClass]);
  const activeCourseId = activeCourse?.id ?? null;

  // Keep state + storage converged on the resolved ids once data loads (handles first
  // paint, stale ids, a school change that resets the class, and course selection).
  useEffect(() => {
    if (activeSchoolId && activeSchoolId !== storedSchoolId) {
      setStoredSchoolId(activeSchoolId);
      persist(SCHOOL_KEY, activeSchoolId);
    }
    if (activeSchoolYearId && activeSchoolYearId !== storedYearId) {
      setStoredYearId(activeSchoolYearId);
      persist(YEAR_KEY, activeSchoolYearId);
    }
    // Only your own class and course are remembered — never a viewed colleague's.
    const ownClassId = ownActiveClass?.id ?? null;
    if (ownClassId && ownClassId !== storedClassId) {
      setStoredClassId(ownClassId);
      persist(CLASS_KEY, ownClassId);
    }
    // Not while the course list loads: the first paint falls back to the active class's
    // course, which must not overwrite a stored choice of another course.
    if (!viewId && activeCourseId && activeCourseId !== storedCourseId && !coursesPending) {
      setStoredCourseId(activeCourseId);
      persist(COURSE_KEY, activeCourseId);
    }
  }, [
    viewId,
    ownActiveClass,
    activeSchoolId,
    activeSchoolYearId,
    activeCourseId,
    storedSchoolId,
    storedYearId,
    storedClassId,
    storedCourseId,
    coursesPending,
  ]);

  const stopViewing = useCallback(() => {
    setViewId(null);
    persistView(null);
  }, []);

  const startViewing = useCallback((classId: string) => {
    setViewId(classId);
    persistView(classId);
  }, []);

  // A stale or unreadable viewed class (deleted, or no longer granted) drops back to your own.
  useEffect(() => {
    if (viewId && !viewQuery.isPending && (!viewQuery.data?.class || ownsViewed)) stopViewing();
  }, [viewId, viewQuery.isPending, viewQuery.data, ownsViewed, stopViewing]);

  const setActiveSchoolId = useCallback(
    (id: string) => {
      stopViewing();
      setStoredSchoolId(id);
      persist(SCHOOL_KEY, id);
      // Reset the active class to the first class in the newly selected school so the class
      // switcher never points at a class from a different school; clear the course so it
      // re-resolves for the new school's year; clear the year so it re-resolves too.
      const firstInSchool = allClasses.find((c) => schoolOf(c).id === id)?.id ?? null;
      setStoredClassId(firstInSchool);
      persist(CLASS_KEY, firstInSchool);
      setStoredCourseId(null);
      persist(COURSE_KEY, null);
      setStoredYearId(null);
      persist(YEAR_KEY, null);
    },
    [allClasses, stopViewing]
  );

  // Pick a school year (from the combined School·Year switcher). A year belongs to one
  // school, so this also sets the school, then resets the class to the first class in that
  // (school, year) — or null for a class-less Department Head — and clears the course so it
  // re-resolves against the year's managed courses.
  const setActiveSchoolYearId = useCallback(
    (yearId: string) => {
      const opt = schoolYearOptions.find((o) => o.yearId === yearId);
      stopViewing();
      setStoredYearId(yearId);
      persist(YEAR_KEY, yearId);
      if (opt) {
        setStoredSchoolId(opt.schoolId);
        persist(SCHOOL_KEY, opt.schoolId);
        const firstInYear =
          allClasses.find(
            (c) => schoolOf(c).id === opt.schoolId && c.course.schoolYear.id === yearId
          )?.id ?? null;
        setStoredClassId(firstInYear);
        persist(CLASS_KEY, firstInYear);
        setStoredCourseId(null);
        persist(COURSE_KEY, null);
      }
    },
    [schoolYearOptions, allClasses, stopViewing]
  );

  const setActiveClassId = useCallback(
    (id: string) => {
      stopViewing();
      setStoredClassId(id);
      persist(CLASS_KEY, id);
      // Selecting a class points the authoring scope at that class's course too, and pins
      // the active year to that class's year (a class implies its school year).
      const cls = allClasses.find((c) => c.id === id);
      const cid = cls?.course.id ?? null;
      setStoredCourseId(cid);
      persist(COURSE_KEY, cid);
      const yid = cls?.course.schoolYear.id ?? null;
      setStoredYearId(yid);
      persist(YEAR_KEY, yid);
    },
    [allClasses, stopViewing]
  );

  const setActiveCourseId = useCallback(
    (id: string) => {
      stopViewing();
      setStoredCourseId(id);
      persist(COURSE_KEY, id);
    },
    [stopViewing]
  );

  const coursesLoading = yearsPending || coursesPending;

  const value = useMemo<ClassContextValue>(
    () => ({
      schools,
      activeSchool,
      activeSchoolId,
      setActiveSchoolId,
      classes,
      activeClass,
      activeClassId,
      setActiveClassId,
      isLoading: isPending || viewPending,
      viewing,
      startViewing,
      stopViewing,
      courses,
      activeCourse,
      activeCourseId,
      setActiveCourseId,
      activeSchoolYearId,
      schoolYearOptions,
      setActiveSchoolYearId,
      coursesLoading,
    }),
    [
      schools,
      activeSchool,
      activeSchoolId,
      setActiveSchoolId,
      classes,
      activeClass,
      activeClassId,
      setActiveClassId,
      isPending,
      viewPending,
      viewing,
      startViewing,
      stopViewing,
      courses,
      activeCourse,
      activeCourseId,
      setActiveCourseId,
      activeSchoolYearId,
      schoolYearOptions,
      setActiveSchoolYearId,
      coursesLoading,
    ]
  );

  return <ClassContext.Provider value={value}>{children}</ClassContext.Provider>;
}

// ----------------------------------------------------------------------

export function useActiveClass(): ClassContextValue {
  const ctx = useContext(ClassContext);
  if (!ctx) throw new Error('useActiveClass must be used within a ClassProvider');
  return ctx;
}

/**
 * The course-authoring scope (Curriculum / Lesson Plans / Standards). Resolves from the
 * active class when the teacher has one, or from school-year membership when they don't —
 * so a class-less Department Head can still select and manage a course. `isLoading` folds
 * in membership + course-list loading (kept separate from `useActiveClass().isLoading`,
 * which stays purely the teaching/`GetMyClasses` state).
 */
export function useActiveCourse() {
  const ctx = useContext(ClassContext);
  if (!ctx) throw new Error('useActiveCourse must be used within a ClassProvider');
  return {
    courses: ctx.courses,
    activeCourse: ctx.activeCourse,
    activeCourseId: ctx.activeCourseId,
    setActiveCourseId: ctx.setActiveCourseId,
    activeSchoolYearId: ctx.activeSchoolYearId,
    isLoading: ctx.isLoading || ctx.coursesLoading,
  };
}

/**
 * Whether the active class can be changed from this page (epic #566): false while a lead views
 * a colleague's class. Live help (pause / resume / message / acknowledge an alert) stays
 * allowed — the server allows the same.
 */
export function useClassAccess() {
  const ctx = useContext(ClassContext);
  if (!ctx) throw new Error('useClassAccess must be used within a ClassProvider');
  return { viewing: ctx.viewing, canEdit: !ctx.viewing, canLiveHelp: true };
}
